import { Session } from '../types/Session';
import { Run, isRunActivelySettling } from '../types/Run';

export interface SessionRunTimeRange {
  start: string;
  end: string;
  tzLabel: string | null;
}

export type RunTimeMode = 'time' | 'datetime';

const TZ_OFFSET_RE = /([+-])(\d{2}):?(\d{2})$/;

// Append a `Z` to naive ISO strings so JavaScript parses them as UTC instead
// of the browser's local zone. Storage policy is UTC; any string without an
// explicit `Z` or `±HH:MM` is assumed UTC.
export function ensureUtcIso(iso: string): string {
  if (/Z$/.test(iso)) return iso;
  if (TZ_OFFSET_RE.test(iso)) return iso;
  return `${iso}Z`;
}

// The server stamps bare-created runs with datetime.max (year 9999) and only
// writes the real start time when processing completes — treat that sentinel
// as "no timestamp yet", never format it as a date.
export function isSentinelRunTs(iso: string | null | undefined): boolean {
  return !!iso && iso.startsWith('9999');
}

// Placeholder run lists render in place of a settling run's timestamp — kept
// here so every list shows the identical label.
export const RUN_PROCESSING_LABEL = 'Processing…';

function pickRunTimestamp(run: RunTimestampFields): string | null {
  return isSentinelRunTs(run.run_ts) ? null : run.run_ts || null;
}

// The fields any timestamp decision needs. Deliberately looser than `Run` so
// the light list payloads (and the `any`-typed rows in older pages) satisfy it.
export interface RunTimestampFields {
  run_ts?: string | null;
  run_status?: number | null;
  timezone?: string | null;
}

/**
 * The single answer to "what goes in this run's timestamp cell".
 *
 * Three states, resolved in one place so every list agrees:
 *  - a real timestamp        → the wall clock at the recording location
 *  - none yet, still settling → RUN_PROCESSING_LABEL
 *  - none and not settling    → 'NA'
 *
 * The last case matters twice over. A run that fails before metadata salvage
 * keeps the year-9999 creation sentinel forever, so keying the label on the
 * sentinel alone would leave it reading "Processing…" for good — and a
 * planned (bare-created) run sits at CREATED indefinitely by design, so the
 * test is `isRunActivelySettling`, not the wider `isRunSettling` band.
 *
 * Display only — print/export/search paths call `formatRunWallClock` directly
 * so a label never lands in a date column.
 */
export function runTimestampLabel(
  run: RunTimestampFields | null | undefined,
  mode: RunTimeMode = 'datetime',
): string {
  if (!run) return 'NA';
  const wallClock = formatRunWallClock(run.run_ts, run.timezone, mode);
  if (wallClock) return wallClock;
  return isRunActivelySettling(run.run_status) ? RUN_PROCESSING_LABEL : 'NA';
}

/**
 * True if the session holds a run that is actively working towards a
 * timestamp. Bare-created (planned) runs do not count: they read as
 * "Planned", which is what the caller shows when this is false.
 */
export function sessionHasSettlingRuns(session: Session | null | undefined): boolean {
  for (const cfg of session?.configs ?? []) {
    for (const run of cfg.runs ?? []) {
      if (!pickRunTimestamp(run) && isRunActivelySettling(run.run_status)) return true;
    }
  }
  return false;
}

function readPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

const WALL_CLOCK_OPTS: Record<RunTimeMode, Intl.DateTimeFormatOptions> = {
  datetime: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  time: { hour: '2-digit', minute: '2-digit', hour12: false },
};

// Constructing Intl.DateTimeFormat dominates formatting cost and run lists
// format one timestamp per row, so cache instances per timezone+mode (a list
// realistically spans one or two timezones).
const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

function wallClockFormatter(timezone: string, mode: RunTimeMode): Intl.DateTimeFormat {
  const key = `${timezone}|${mode}`;
  let formatter = wallClockFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', { ...WALL_CLOCK_OPTS[mode], timeZone: timezone });
    wallClockFormatters.set(key, formatter);
  }
  return formatter;
}

export function formatRunWallClock(
  iso: string | null | undefined,
  timezone: string | null | undefined,
  mode: RunTimeMode = 'time',
): string {
  if (!iso || isSentinelRunTs(iso)) return '';

  const date = new Date(ensureUtcIso(iso));
  const isValidDate = !Number.isNaN(date.getTime());

  if (isValidDate && timezone) {
    try {
      const parts = wallClockFormatter(timezone, mode).formatToParts(date);
      const hour = readPart(parts, 'hour');
      const minute = readPart(parts, 'minute');
      if (mode === 'datetime') {
        const year = readPart(parts, 'year');
        const month = readPart(parts, 'month');
        const day = readPart(parts, 'day');
        return `${year}-${month}-${day} ${hour}:${minute}`;
      }
      return `${hour}:${minute}`;
    } catch {
      // Falls through to ISO-slice if timezone is malformed.
    }
  }

  if (mode === 'datetime') {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
    if (m) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
  }
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[1]}:${m[2]}` : iso;
}

// Return an ISO-shaped string (`yyyy-MM-ddTHH:mm:ss`, no offset) whose
// components are the wall-clock components in `timezone`. Designed for Plotly
// date axes: Plotly parses naive ISO strings without applying any timezone
// shift, so tick labels render exactly these components in every browser.
export function toRecordingZoneIso(date: Date, timezone: string | null | undefined): string {
  if (Number.isNaN(date.getTime())) return '';
  if (!timezone) return date.toISOString().replace(/Z$/, '');
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const year = readPart(parts, 'year');
    const month = readPart(parts, 'month');
    const day = readPart(parts, 'day');
    const hour = readPart(parts, 'hour') === '24' ? '00' : readPart(parts, 'hour');
    const minute = readPart(parts, 'minute');
    const second = readPart(parts, 'second');
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  } catch {
    return date.toISOString().replace(/Z$/, '');
  }
}

function shortOffsetForZone(timezone: string, sampleDate: Date): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(sampleDate);
    const name = readPart(parts, 'timeZoneName');
    return name || null;
  } catch {
    return null;
  }
}

function offsetFromIso(iso: string): string | null {
  if (/Z$/.test(iso)) return 'UTC';
  const m = TZ_OFFSET_RE.exec(iso);
  if (!m) return null;
  return `UTC${m[1]}${m[2]}:${m[3]}`;
}

function deriveSessionTimezone(runs: Run[]): string | null {
  for (const r of runs) {
    if (r.timezone) return r.timezone;
  }
  return null;
}

export function getSessionRunTimeRange(session: Session | null | undefined): SessionRunTimeRange | null {
  if (!session) return null;
  const runs: Run[] = [];
  for (const cfg of session.configs ?? []) {
    for (const run of cfg.runs ?? []) {
      if (pickRunTimestamp(run)) runs.push(run);
    }
  }
  if (runs.length === 0) return null;

  runs.sort((a, b) => {
    const ta = Date.parse(ensureUtcIso(pickRunTimestamp(a) as string));
    const tb = Date.parse(ensureUtcIso(pickRunTimestamp(b) as string));
    return ta - tb;
  });

  const firstIso = pickRunTimestamp(runs[0]) as string;
  const lastIso = pickRunTimestamp(runs[runs.length - 1]) as string;
  const timezone = deriveSessionTimezone(runs);

  const start = formatRunWallClock(firstIso, timezone, 'time');
  const end = formatRunWallClock(lastIso, timezone, 'time');
  if (!start || !end) return null;

  let tzLabel: string | null = null;
  if (timezone) {
    const firstDate = new Date(ensureUtcIso(firstIso));
    tzLabel = shortOffsetForZone(timezone, Number.isNaN(firstDate.getTime()) ? new Date() : firstDate);
  }
  if (!tzLabel) tzLabel = offsetFromIso(firstIso);

  return { start, end, tzLabel };
}
